import { Duration, Instant } from "@js-joda/core";
import {
  durationJsonFormat,
  instantJsonFormat,
} from "@redotech/json-extra/joda";
import {
  JsonFormat,
  arrayJsonFormat,
  nullableJsonFormat,
  numberJsonFormat,
  objectJsonFormat,
  stringJsonFormat,
  symbolJsonFormat,
  symbolUnionJsonFormat,
} from "@redotech/json/format";

/**
 * Product condition
 */
export type Condition =
  | Condition.DiscountCode
  | Condition.FulfillmentPeriod
  | Condition.ShippingWithin
  | Condition.OrderDate
  | Condition.OrderWithin
  | Condition.ProductTag
  | Condition.CustomerTag
  | Condition.PriceLessThan;

export namespace Condition {
  export const CUSTOMER_TAG = Symbol("customer_tag");
  export const DELIVERY_WITHIN = Symbol("shipping_within");
  export const DISCOUNT_CODE = Symbol("discount_code");
  export const FULFILLMENT_WITHIN = Symbol("fulfilment_within");
  export const ORDER_DATE = Symbol("order_date");
  export const ORDER_WITHIN = Symbol("order_within");
  export const PRICE_LESS_THAN = Symbol("price_less_than");
  export const PRODUCT_TAG = Symbol("product_tag");

  export interface CustomerTag {
    type: typeof CUSTOMER_TAG;
    tags: readonly string[];
  }

  export const customerTagJsonFormat: JsonFormat<CustomerTag> =
    objectJsonFormat(
      {
        type: symbolJsonFormat(CUSTOMER_TAG),
        tags: arrayJsonFormat(stringJsonFormat),
      },
      {},
    );

  export interface DiscountCode {
    type: typeof DISCOUNT_CODE;
    codes: readonly string[];
  }

  export const discountCodeJsonFormat: JsonFormat<DiscountCode> =
    objectJsonFormat(
      {
        type: symbolJsonFormat(DISCOUNT_CODE),
        codes: arrayJsonFormat(stringJsonFormat),
      },
      {},
    );

  export interface FulfillmentPeriod {
    type: typeof FULFILLMENT_WITHIN;
    duration: Duration;
  }

  export const fulfillmentPeriodJsonFormat: JsonFormat<FulfillmentPeriod> =
    objectJsonFormat(
      {
        type: symbolJsonFormat(FULFILLMENT_WITHIN),
        duration: durationJsonFormat,
      },
      {},
    );

  export interface OrderDate {
    type: typeof ORDER_DATE;
    start: Instant | null;
    end: Instant | null;
  }

  export const orderDateJsonFormat: JsonFormat<OrderDate> = objectJsonFormat(
    {
      type: symbolJsonFormat(ORDER_DATE),
      start: nullableJsonFormat(instantJsonFormat),
      end: nullableJsonFormat(instantJsonFormat),
    },
    {},
  );

  export interface OrderWithin {
    type: typeof ORDER_WITHIN;
    duration: Duration;
  }

  export const orderWithinFormat: JsonFormat<OrderWithin> = objectJsonFormat(
    { type: symbolJsonFormat(ORDER_WITHIN), duration: durationJsonFormat },
    {},
  );

  export interface PriceLessThan {
    type: typeof PRICE_LESS_THAN;
    amount: number;
  }

  export const priceLessThanJsonFormat: JsonFormat<PriceLessThan> =
    objectJsonFormat(
      {
        type: symbolJsonFormat(PRICE_LESS_THAN),
        amount: numberJsonFormat,
      },
      {},
    );

  export interface ProductTag {
    type: typeof PRODUCT_TAG;
    tags: readonly string[];
  }

  export const productTagJson: JsonFormat<ProductTag> = objectJsonFormat(
    {
      type: symbolJsonFormat(PRODUCT_TAG),
      tags: arrayJsonFormat(stringJsonFormat),
    },
    {},
  );

  export interface ShippingWithin {
    type: typeof DELIVERY_WITHIN;
    duration: Duration;
  }

  export const deliveryWithinJson: JsonFormat<ShippingWithin> =
    objectJsonFormat(
      {
        type: symbolJsonFormat(DELIVERY_WITHIN),
        duration: durationJsonFormat,
      },
      {},
    );
}

export const conditionJsonFormat: JsonFormat<Condition> = symbolUnionJsonFormat(
  "type",
  "type",
  {
    [Condition.CUSTOMER_TAG]: Condition.customerTagJsonFormat,
    [Condition.DELIVERY_WITHIN]: Condition.deliveryWithinJson,
    [Condition.DISCOUNT_CODE]: Condition.discountCodeJsonFormat,
    [Condition.FULFILLMENT_WITHIN]: Condition.fulfillmentPeriodJsonFormat,
    [Condition.ORDER_DATE]: Condition.orderDateJsonFormat,
    [Condition.ORDER_WITHIN]: Condition.orderWithinFormat,
    [Condition.PRICE_LESS_THAN]: Condition.priceLessThanJsonFormat,
    [Condition.PRODUCT_TAG]: Condition.productTagJson,
  },
);

export interface Parameters {
  discountCodes: string[];
  now: Instant;
  fulfilledOn: Instant;
  deliveredOn: Instant;
  orderedOn: Instant;
  customerTags: string[];
  price: number;
  productTags: string[];
}

export namespace Condition {
  export function evaluate(
    condition: Condition,
    parameters: Parameters,
  ): boolean {
    switch (condition.type) {
      case Condition.CUSTOMER_TAG:
        return condition.tags.some((tag) =>
          parameters.customerTags.includes(tag),
        );
      case Condition.DISCOUNT_CODE:
        return condition.codes.some((code) =>
          parameters.discountCodes.includes(code),
        );
      case Condition.FULFILLMENT_WITHIN:
        return parameters.now.isBefore(
          parameters.fulfilledOn.plus(condition.duration),
        );
      case Condition.ORDER_DATE:
        return (
          (condition.start == null ||
            !condition.start.isAfter(parameters.now)) &&
          (condition.end == null || parameters.now.isBefore(condition.end))
        );
      case Condition.ORDER_WITHIN:
        return parameters.now.isBefore(
          parameters.orderedOn.plus(condition.duration),
        );
      case Condition.PRICE_LESS_THAN:
        return parameters.price < condition.amount;
      case Condition.PRODUCT_TAG:
        const productTags =
          parameters.productTags?.map((tag) => tag.toLowerCase()) || [];
        return condition.tags.some((tag) =>
          productTags.includes(tag.toLowerCase()),
        );
      case Condition.DELIVERY_WITHIN:
        return parameters.now.isBefore(
          parameters.deliveredOn.plus(condition.duration),
        );
    }
  }
}
